import React, { useEffect, useRef, useState } from 'react';
import MapControls from '../MapControls/MapControl';
import geoFences from '../../assets/geocercas/geofences.json'
import { getGeofenceCategory } from '../../services/geofences';

const MODEL_YAW_OFFSET_DEG = 0; // change to +90/180 if your GLB still looks sideways

const SimpleCesiumFallback = ({ trucks = [] }) => {
  const cesiumContainer = useRef(null);
  const [viewer, setViewer] = useState(null);
  const hasInitialCameraSet = useRef(false);

  // --- NEW: refs for geofences ---
  const selectedGeofenceRef = useRef(null);
  const selectedOutlineRef = useRef(null);
  const geofenceDsRef = useRef(null);
  const hasGeofenceCameraFit = useRef(false);
  const truckIdsRef = useRef(new Set()); // ids of truck entities we created

  // === Fetch trucks from your API ===
  const fetchTruckData = async () => {
    try {
      const auth = btoa(`${process.env.REACT_APP_USER}:${process.env.REACT_APP_PASS}`);
      const header = `Basic ${auth}`;
      const endpoint = process.env.REACT_APP_DATA_ENDPOINT;

      if (!endpoint) {
        console.warn('Missing REACT_APP_DATA_ENDPOINT');
        return;
      }

      const res = await fetch(endpoint, {
        method: 'GET',
        headers: { Authorization: header },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Adapt trucks
      const adapted = data.map((t, idx) => ({
        id: t.device_id || `truck-${idx}`,
        name: t.truckPatent || t.marca || `Truck ${idx}`,
        lat: Number(t.lat),
        lon: Number(t.lon),
        altitude: 2400,
        speed: Number(t.velocidad) || 0, // km/h
        status: Number(t.velocidad) > 0 ? 'moving' : 'parked',
        driver: t.operador || 'Sin Operador',
        brand: t.marca,
        model: t.modelo,
        lastUpdate: t.currTime,
        heading: Number(t.head) || 0, // degrees, 0=N, 90=E
      }));
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  // === Cesium viewer ===
  useEffect(() => {
    if (!cesiumContainer.current || !window.Cesium) return;

    // Set ion token BEFORE creating Viewer
    const ionToken = process.env.api_endpoint_token || process.env.REACT_APP_CESIUM_ION_TOKEN;
    if (!ionToken) {
      console.warn('Cesium ion token missing (process.env.api_endpoint_token / REACT_APP_CESIUM_ION_TOKEN).');
    } else {
      window.Cesium.Ion.defaultAccessToken = ionToken;
    }

    const v = new window.Cesium.Viewer(cesiumContainer.current, {
      animation: false,
      timeline: false,
      fullscreenButton: false,
      vrButton: false,
      geocoder: false,
      homeButton: true,
      sceneModePicker: false,
      baseLayerPicker: false,
      navigationHelpButton: false,
      infoBox: true,
      selectionIndicator: false,
      imageryProvider: window.Cesium.createWorldImagery({
        style: window.Cesium.IonWorldImageryStyle.AERIAL_WITH_LABELS
      }),
      terrainProvider: window.Cesium.createWorldTerrain(),
      shouldAnimate: false,
    });

    v.scene.globe.depthTestAgainstTerrain = true;
    v.scene.globe.enableLighting = true;   // sunlight over terrain
    v.shadows = true;                       // cast model shadows
    v.scene.shadowMap.enabled = true;

    v.clock.shouldAnimate = false;
    setViewer(v);

    return () => !v.isDestroyed() && v.destroy();
  }, []);

  // --- NEW: Load geofences (GeoJSON) once viewer exists ---
  useEffect(() => {
    if (!viewer || !window.Cesium) return;
    const Cesium = window.Cesium;

    const loadGeofences = async (source) => {
      try {
        // remove previous layer if reloading
        if (geofenceDsRef.current) {
          viewer.dataSources.remove(geofenceDsRef.current, true);
          geofenceDsRef.current = null;
        }

        // --- Enrich incoming features with fill/border/category ---
        const withFillColor = {
          type: 'FeatureCollection',
          features: source.features.map((d) => {
            // support either speed_limit_up or limite_de_
            const speedLimitUp = d.properties?.speed_limit_up ?? d.properties?.limite_de_;
            const speedValue = speedLimitUp !== undefined ? Number(speedLimitUp) : 150;
            const cat = getGeofenceCategory(speedValue);
            return {
              ...d,
              properties: {
                ...d.properties,
                fill: cat.fill,
                border: cat.border,
                geofenceType: cat.category,
                maxV: d.properties?.speed_limit_up ?? 'No Aplica',
                fullName: d.properties?.name ?? d.properties?.Name ?? d.properties?.nombre_de_ ?? '',
              },
            };
          }),
        };

        // Load GeoJSON; clampToGround for terrain drape (outlines unsupported on ground)
        const ds = await Cesium.GeoJsonDataSource.load(withFillColor, {
          clampToGround: true,
          // IMPORTANT: outlines on ground are not supported; use fill only
          stroke: undefined,
          strokeWidth: undefined,
          fill: Cesium.Color.YELLOW.withAlpha(0.25),
          fill: Cesium.Color.fromCssColorString('#14b7ee').withAlpha(0.25),
        });

        geofenceDsRef.current = ds;
        await viewer.dataSources.add(ds);

        // Click to select geofence: draw ground-clamped outline and open InfoBox
        const handler = new window.Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
        handler.setInputAction((movement) => {
          const pick = viewer.scene.pick(movement.position);
          if (!pick || !pick.id) return;
          const entity = pick.id;
          if (!ds.entities.contains(entity)) return; // only react to this GeoJSON layer

          // Remove previous outline
          if (selectedOutlineRef.current) {
            viewer.entities.remove(selectedOutlineRef.current);
            selectedOutlineRef.current = null;
          }
          selectedGeofenceRef.current = entity;

          // Build outline positions from the polygon hierarchy
          const h = entity.polygon?.hierarchy?.getValue?.(viewer.clock.currentTime);
          const pts = (h?.positions ?? h ?? []);
          if (Array.isArray(pts) && pts.length) {
            // Close the loop
            const positions = pts[0] === pts[pts.length - 1] ? pts : [...pts, pts[0]];
            selectedOutlineRef.current = viewer.entities.add({
              polyline: {
                positions,
                clampToGround: true,
                width: 3,
                material: window.Cesium.Color.fromCssColorString(
                  (selectedGeofenceRef.current?.properties?.border?.getValue?.() ??
                    selectedGeofenceRef.current?.properties?.border ??
                    '#FFFFFF').toString()
                ),
              }
            });
          }

          // Show InfoBox for the selected entity (without selectionIndicator)
          viewer.selectedEntity = entity;
        }, window.Cesium.ScreenSpaceEventType.LEFT_CLICK);

        // Clean up handler on reload/unmount
        viewer._geofenceClickHandler?.destroy?.();
        viewer._geofenceClickHandler = handler;

        // Per-entity styling using properties
        const entities = ds.entities.values;
        for (const e of entities) {
          if (!e.polygon) continue;

          const props = e.properties || {};
          const type = (props.geofence_type?.getValue?.() || props.geofence_type || props.geofenceType || '').toString().toLowerCase();
          const activeStr = (props.isActive?.getValue?.() ?? props.isActive ?? 'True').toString().toLowerCase();
          const active = activeStr === 'true';

          // Use the enriched fill/border colors
          const fillHex = props.fill?.getValue?.() ?? props.fill ?? '#14b7ee';
          const borderHex = props.border?.getValue?.() ?? props.border ?? '#FFFFFF';
          const fillColor = Cesium.Color.fromCssColorString(String(fillHex));
          const borderColor = Cesium.Color.fromCssColorString(String(borderHex));

          e.polygon.material = fillColor.withAlpha(active ? 0.32 : 0.12);
          e.polygon.outline = false; // avoid terrain-outline warning
          e.polygon.zIndex = 0;

          // Popup description
          // Optional: label that respects depth (no hovering)
          // (You can remove this block if you don't want labels)
          try {
            const h = e.polygon.hierarchy.getValue?.(viewer.clock.currentTime);
            const pts = (h?.positions ?? h ?? []);
            if (Array.isArray(pts) && pts.length) {
              const bs = window.Cesium.BoundingSphere.fromPoints(pts);
              if (bs && Number.isFinite(bs.center.x)) {
                e.position = bs.center;
                e.label = new window.Cesium.LabelGraphics({
                  text: (props.name?.getValue?.() || props.name || e.name || 'Geofence').toString(),
                  verticalOrigin: window.Cesium.VerticalOrigin.BOTTOM,
                  pixelOffset: new window.Cesium.Cartesian2(0, -8),
                  // Respect terrain (no “always on top”)
                  disableDepthTestDistance: 0
                });
              }
            }
          } catch { }

          // Popup description (InfoBox)
          const up = (k) => props[k]?.getValue?.() ?? props[k];
          const name = up('fullName') || up('name') || e.name || 'Geofence';
          const typeStr = up('geofenceType') || up('geofence_type') || '—';
          const sUp = up('maxV') ?? up('speed_limit_up') ?? '—';
          const sDown = up('speed_limit_down') ?? '—';
          e.description = `
            <div>
              <strong>${name}</strong><br/>
              Type: ${typeStr}<br/>
              Active: ${active ? 'Yes' : 'No'}<br/>
              Speed up/down: ${sUp} / ${sDown} km/h
            </div>
          `;
        }

        // Fit camera to geofences only once (won't snap on refresh)
        if (!hasGeofenceCameraFit.current) {
          hasGeofenceCameraFit.current = true;
          try { await viewer.flyTo(ds, { duration: 0.0 }); } catch { }
        }
      } catch (err) {
        console.error('Failed to load geofences:', err);
      }
    };

    // Load from a static file in /public
    loadGeofences(geoFences);
  }, [viewer]);

  // === Add/Update trucks (fixed position + heading only) ===
  useEffect(() => {
    if (!viewer || !window.Cesium) return;

    const Cesium = window.Cesium;

    const isValidLonLat = (lon, lat) =>
      Number.isFinite(lon) && Number.isFinite(lat) &&
      lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;

    // keep track of ids we saw this render
    const seen = new Set();

    trucks.forEach((truck) => {
      if (!isValidLonLat(truck.lon, truck.lat)) return; // guard bad data
      seen.add(truck.id);
      let entity = viewer.entities.getById(truck.id);

      const position = Cesium.Cartesian3.fromDegrees(truck.lon, truck.lat, truck.altitude);
      const orientation = Cesium.Transforms.headingPitchRollQuaternion(
        position,
        new Cesium.HeadingPitchRoll(
          Cesium.Math.toRadians(truck.heading + MODEL_YAW_OFFSET_DEG),
          0,
          0
        )
      );

      if (!entity) {
        entity = viewer.entities.add({
          id: truck.id,
          name: truck.name,
          position,
          orientation,
          model: {
            uri: '/models/haul_truck.glb',
            minimumPixelSize: 64,
            maximumScale: 100,
            scale: 0.1,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          },
          description: `
            <div>
              <strong>${truck.name}</strong><br/>
              Driver: ${truck.driver}<br/>
              Speed: ${truck.speed} km/h<br/>
              Status: ${truck.status}<br/>
              Brand: ${truck.brand}<br/>
              Model: ${truck.model}
            </div>
          `,
        });
        truckIdsRef.current.add(truck.id);

      } else {
        // Update fixed pose
        entity.position = position;
        entity.orientation = orientation;
        truckIdsRef.current.add(truck.id);
      }
    });

    // REMOVE trucks that are no longer in the filtered list
    for (const id of Array.from(truckIdsRef.current)) {
      if (!seen.has(id)) {
        const e = viewer.entities.getById(id);
        if (e) viewer.entities.remove(e);
        truckIdsRef.current.delete(id);
      }
    }

    // Initial camera fly-to on first valid truck (only once)
    if (!hasInitialCameraSet.current && trucks.length > 0) {
      const t0 = trucks.find(t =>
        Number.isFinite(t.lon) && Number.isFinite(t.lat) &&
        t.lon >= -180 && t.lon <= 180 && t.lat >= -90 && t.lat <= 90
      );
      if (t0) {
        viewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(t0.lon, t0.lat, 3000),
          orientation: {
            heading: Cesium.Math.toRadians(0),
            pitch: Cesium.Math.toRadians(-30),
            roll: 0,
          },
        });
        hasInitialCameraSet.current = true;
      }
    }
  }, [viewer, trucks]);

  return (
    <div
      ref={cesiumContainer}
      style={{ width: '100%', height: '600px', border: '1px solid #ccc' }}
    />
  );
};

export default SimpleCesiumFallback;
